import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Loader from 'react-ts-loaders';
import { classNames } from '../../../../../shared/libs/classNames';
import css from './CreateWorkshopModuleModal.module.scss';
import Button, { buttonsTypes } from '../../../../../components/ui/button/button';
import { SelectModuleCategories } from '../select-categories/SelectModuleCategories';
import { SelectModuleTags } from '../select-tags/SelectModuleTags';
import { SetModuleName } from '../set-name/SetModuleName';
import { SelectModuleEntities } from '../select-entities/SelectModuleEntities';
import { useActionCreators } from '../../../../../shared';
import { NewModal } from '../../../../../components/ui/modals/new-modal';
import { useCreateWorkshopModule } from '../../../model/services/create-module/useCreateWorkshopModule';
import { SetModuleDescription } from '../set-description/SetModuleDescription';
import OpenArrowIcon from '../../../assets/open-arrow.svg';
import CloseArrowIcon from '../../../assets/close-arrow.svg';
import {
	createWorkshopModuleActions, createWorkshopModuleSelectors,
	createWorkshopModuleSlice,
} from '../../../model/create-module/slice/createWorkshopModuleSlice';
import { useAppDispatch, useAppSelector } from '../../../../../shared/libs/redux';
import PublishGalleryCheckbox from '../../publish-gallery-checkbox/PublishGalleryCheckbox';
import {
	workshopModuleActions,
	workshopModuleSelectors,
} from '../../../../../entities/module/workshop-module';
import {
	usePublishTariffSelected,
} from '../../../../workshop/templates/publish-to-gallery/libs/usePublishTariffSelected';
import { Tariff } from '../../../../../entities/tariff/model/data';
import { usePublishWorkshopModule } from '../../../model/services/publish-module/usePublishWorkshopModule';
import { notificationSuccess } from '../../../../../components/Notifications/callNotifcation';
import { useModulePreview } from '../../../../../shared/libs/module/useModulePreview';
import { setCreateModuleWindowPreview } from '../../../../../entities/module/private-template-module/model/slice';
import { templateGalleryModuleApi } from '../../../../../entities/template-gallery-module';

interface ICreateWorkshopModuleModalProps {
	className?: string,
}

export const CreateWorkshopModuleModal = ({ className }: ICreateWorkshopModuleModalProps) => {
	const dispatch = useAppDispatch();
	const [isPublishingInGallery, setIsPublishingInGallery] = useState(true);
	const isOpen = useSelector(createWorkshopModuleSlice.selectors.getIsWindowOpen);
	const moduleId = useSelector(workshopModuleSelectors.getPublishingModuleId);
	const selectedCategories = useSelector(createWorkshopModuleSlice.selectors.getSelectedCategoriesIds);
	const name = useSelector(createWorkshopModuleSlice.selectors.getName);
	const selectedTags = useSelector(createWorkshopModuleSlice.selectors.getSelectedTagsIds);
	const selectedLegalEntities = useSelector(createWorkshopModuleSlice.selectors.getSelectedLegalEntitiesIds);
	const isBtnActive = useSelector(createWorkshopModuleSlice.selectors.getIsBtnActive);
	const { imageUrl, isImageLoading, setIsImageLoading } = useModulePreview();

	const actions = useActionCreators(createWorkshopModuleActions);
	const { onCreateModule } = useCreateWorkshopModule();
	const { selectTariffs, currentPermission } = usePublishTariffSelected();
	const { onPublishModule, requestData } = usePublishWorkshopModule();

	const [showMore, setShowMore] = useState(false);

	useEffect(() => {
		if (currentPermission > 0 && moduleId) {
			onPublishModule(currentPermission);
		}
	}, [currentPermission, moduleId]);

	const createModuleWithPublishing = async () => {
		const moduleId = await onCreateModule();

		if (moduleId && isPublishingInGallery) {
			notificationSuccess('Модуль успешно создан');

			dispatch(workshopModuleActions.setPublishingModuleId(moduleId));
			dispatch(templateGalleryModuleApi.util?.invalidateTags(['templateGalleryModule']));

			const tariffsToSelect = [
				{ tariff: Tariff.BASIC, bitPosition: 0 },
				{ tariff: Tariff.TRIAL, bitPosition: 1 },
			];
			selectTariffs(tariffsToSelect);
		} else {
			selectTariffs([]);
		}
	};

	const editor = useAppSelector(createWorkshopModuleSelectors.getEditor);

	const handleModuleModal = (value: boolean) => {
		dispatch(createWorkshopModuleActions.setIsWindowOpen({ editor, active: value }));
	};

	useEffect(() => {
		if (!isOpen) {
			dispatch(createWorkshopModuleActions.cleanStateCreatingData());
			dispatch(setCreateModuleWindowPreview(''));
			setIsPublishingInGallery(true);
		}
	}, [isOpen, dispatch]);

	return (
		<NewModal active={isOpen} closeFunc={(value) => handleModuleModal(value)}>
			<div className={classNames(css.createTemplateWrapper, {}, [className])}>
				{isImageLoading && <Loader className={css.loader} type="ring" color="#8e9db0" size={148} />}

				{imageUrl && (
					<img
						src={imageUrl}
						alt="Base64"
						className={css.preview}
						onLoad={() => setIsImageLoading(false)}
						onError={() => setIsImageLoading(false)}
						style={{ display: isImageLoading ? 'none' : 'block' }}
					/>
				)}

				<div className={css.wrapper}>
					<div className={css.content}>
						<div className={css.title}>Создать модуль</div>
						<SetModuleName name={name} handleName={actions.setName} />
						<SelectModuleCategories
							selectCategory={actions.setSelectedCategoriesIds}
							selectedCategories={selectedCategories}
							unselectCategory={actions.removeSelectedCategoryId}
						/>
						<SelectModuleTags
							selectedTags={selectedTags}
							selectTag={actions.addSelectedTagId}
							unselectTag={actions.removeSelectedTag}
						/>
						<div
							className={css.more}
							onClick={() => setShowMore(!showMore)}
							role="presentation"
						>
							<div className={css.text}>Дополнительно</div>
							{showMore ? <OpenArrowIcon /> : <CloseArrowIcon />}
						</div>
						{showMore && (
							<>
								<SelectModuleEntities
									selectedLegalEntityId={selectedLegalEntities}
									selectLegalEntity={actions.addSelectedLegalEntity}
									unselectLegalEntity={actions.removeSelectedLegalEntity}
								/>
								<SetModuleDescription />
								<PublishGalleryCheckbox
									onChange={setIsPublishingInGallery}
									isChoiced={isPublishingInGallery}
								/>
							</>
						)}
						<Button
							text="Создать модуль"
							onClick={createModuleWithPublishing}
							buttonType={buttonsTypes.BRIGHT}
							cssClass={css.btn}
							inActive={isBtnActive && !requestData.isLoading}
						/>
					</div>
				</div>
			</div>
		</NewModal>
	);
};
